const LAYOUT_GAP_X = 60;
const LAYOUT_GAP_Y = 60;
const NODE_W = 160;
const NODE_H = 60;
const NODE_W_WIDE = 200;
const NODE_H_TALL = 90;
export const layoutNodes = (model) => {
    switch (model.type) {
        case 'sequence':
            return layoutSequence(model);
        case 'gantt':
            return layoutGantt(model);
        case 'c4Context':
        case 'c4Container':
        case 'c4Component':
        case 'c4Dynamic':
        case 'c4Deployment':
            return layoutC4(model);
        case 'journey':
            return layoutJourney(model);
        case 'mindmap':
            return layoutMindmap(model);
        case 'class':
            return layoutClass(model);
        case 'pie':
            return layoutList(model, true);
        case 'radar':
            return layoutList(model, false);
        case 'er':
            return layoutHierarchical(model);
        case 'state':
            return layoutHierarchical(model);
        default:
            return layoutHierarchical(model);
    }
};
export const totalBounds = (positions, connectors) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const b of positions.values()) {
        if (b.x < minX)
            minX = b.x;
        if (b.y < minY)
            minY = b.y;
        if (b.x + b.width > maxX)
            maxX = b.x + b.width;
        if (b.y + b.height > maxY)
            maxY = b.y + b.height;
    }
    for (const c of connectors) {
        if (c.x1 < minX)
            minX = c.x1;
        if (c.y1 < minY)
            minY = c.y1;
        if (c.x2 < minX)
            minX = c.x2;
        if (c.y2 < minY)
            minY = c.y2;
        if (c.x1 > maxX)
            maxX = c.x1;
        if (c.y1 > maxY)
            maxY = c.y1;
        if (c.x2 > maxX)
            maxX = c.x2;
        if (c.y2 > maxY)
            maxY = c.y2;
    }
    if (!isFinite(minX))
        return { x: 0, y: 0, width: 0, height: 0 };
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};
const layoutHierarchical = (model) => {
    const positions = new Map();
    const layers = topologicalLayers(model);
    const layerWidths = layers.map((l) => l.length * (NODE_W + LAYOUT_GAP_X));
    const totalWidth = Math.max(...layerWidths, NODE_W);
    const colX = layers.map((_, i) => i * (NODE_W + LAYOUT_GAP_X * 2) + LAYOUT_GAP_X);
    layers.forEach((layer, i) => {
        const layerWidth = layer.length * (NODE_W + LAYOUT_GAP_X);
        const offsetX = colX[i];
        layer.forEach((id, j) => {
            const node = model.nodes.find((n) => n.id === id);
            const height = node?.shape === 'cylinder' || node?.shape === 'person' ? NODE_H_TALL : NODE_H;
            positions.set(id, {
                x: offsetX,
                y: j * (height + LAYOUT_GAP_Y) + LAYOUT_GAP_Y,
                width: NODE_W,
                height,
            });
        });
        void totalWidth;
    });
    return positions;
};
const topologicalLayers = (model) => {
    const layers = [];
    const placed = new Set();
    const indeg = new Map();
    for (const n of model.nodes)
        indeg.set(n.id, 0);
    for (const e of model.edges) {
        if (indeg.has(e.from) && indeg.has(e.to))
            indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
    }
    let frontier = model.nodes.filter((n) => (indeg.get(n.id) ?? 0) === 0).map((n) => n.id);
    if (frontier.length === 0 && model.nodes.length)
        frontier = [model.nodes[0].id];
    while (frontier.length) {
        layers.push([...frontier]);
        for (const id of frontier)
            placed.add(id);
        const next = new Set();
        for (const e of model.edges) {
            if (placed.has(e.from) && !placed.has(e.to))
                next.add(e.to);
        }
        frontier = Array.from(next);
    }
    // Stragglers: any node not placed.
    const stragglers = model.nodes.filter((n) => !placed.has(n.id)).map((n) => n.id);
    if (stragglers.length)
        layers.push(stragglers);
    return layers;
};
const layoutSequence = (model) => {
    const positions = new Map();
    const actors = model.nodes.filter((n) => n.layer === 'actor' || !n.layer);
    const actorsPerRow = Math.min(actors.length, 5);
    const cellW = NODE_W_WIDE;
    actors.forEach((n, idx) => {
        const col = idx % actorsPerRow;
        const row = Math.floor(idx / actorsPerRow);
        positions.set(n.id, {
            x: col * (cellW + LAYOUT_GAP_X) + LAYOUT_GAP_X,
            y: row * 240 + LAYOUT_GAP_Y,
            width: cellW,
            height: NODE_H,
        });
    });
    // Notes become standalone notes layered under messages.
    const notes = model.meta.notes ?? [];
    notes.forEach((note, idx) => {
        positions.set(`note_${idx}`, {
            x: LAYOUT_GAP_X + idx * 40,
            y: 200 + idx * 40,
            width: NODE_W_WIDE,
            height: NODE_H,
        });
    });
    return positions;
};
const layoutGantt = (model) => {
    const positions = new Map();
    const tasks = model.meta.tasks ?? [];
    let y = LAYOUT_GAP_Y;
    let lastSection = '';
    for (const task of tasks) {
        if (task.section !== lastSection) {
            y += NODE_H;
            lastSection = task.section;
        }
        positions.set(task.id, { x: LAYOUT_GAP_X + 200, y, width: NODE_W * 2, height: NODE_H });
        y += NODE_H;
    }
    return positions;
};
const C4_TIER_ORDER = ['person', 'system', 'container', 'component', 'node', 'default'];
const layoutC4 = (model) => {
    const positions = new Map();
    const tiers = new Map();
    for (const n of model.nodes) {
        const tier = String(n.meta?.['tier'] ?? n.layer ?? 'default');
        if (!tiers.has(tier))
            tiers.set(tier, []);
        tiers.get(tier).push(n.id);
    }
    const orderedTiers = Array.from(tiers.entries()).sort(([a], [b]) => {
        const ai = C4_TIER_ORDER.indexOf(a);
        const bi = C4_TIER_ORDER.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
    let y = LAYOUT_GAP_Y;
    for (const [, ids] of orderedTiers) {
        ids.forEach((id, idx) => {
            const node = model.nodes.find((n) => n.id === id);
            const h = node?.shape === 'person' || node?.shape === 'cylinder' ? NODE_H_TALL : NODE_H;
            positions.set(id, { x: idx * (NODE_W_WIDE + LAYOUT_GAP_X) + LAYOUT_GAP_X, y, width: NODE_W_WIDE, height: h });
        });
        y += NODE_H_TALL + LAYOUT_GAP_Y;
    }
    return positions;
};
const layoutJourney = (model) => {
    const positions = new Map();
    const grouped = new Map();
    for (const n of model.nodes) {
        const p = n.parent ?? '_';
        if (!grouped.has(p))
            grouped.set(p, []);
        grouped.get(p).push(n.id);
    }
    let y = LAYOUT_GAP_Y;
    for (const g of grouped.values()) {
        g.forEach((id, idx) => {
            positions.set(id, { x: LAYOUT_GAP_X + idx * (NODE_W_WIDE + LAYOUT_GAP_X), y, width: NODE_W_WIDE, height: NODE_H });
        });
        y += NODE_H + LAYOUT_GAP_Y;
    }
    return positions;
};
const layoutMindmap = (model) => {
    const positions = new Map();
    const levels = new Map();
    for (const n of model.nodes) {
        const level = model.nodes.filter((m) => m.id === n.parent).length === 0 ? 0 : 1;
        if (!levels.has(level))
            levels.set(level, []);
        levels.get(level).push(n.id);
    }
    const orderedLevels = Array.from(levels.entries()).sort(([a], [b]) => a - b);
    orderedLevels.forEach(([level, ids], li) => {
        ids.forEach((id, idx) => {
            const width = NODE_W - level * 20;
            positions.set(id, {
                x: level * (NODE_W + LAYOUT_GAP_X) + LAYOUT_GAP_X,
                y: idx * (NODE_H + LAYOUT_GAP_Y) + LAYOUT_GAP_Y + li * 200,
                width,
                height: NODE_H,
            });
        });
    });
    return positions;
};
const layoutClass = (model) => {
    return layoutHierarchical(model);
};
const layoutList = (model, vertical) => {
    const positions = new Map();
    model.nodes.forEach((n, idx) => {
        if (vertical) {
            positions.set(n.id, {
                x: LAYOUT_GAP_X,
                y: LAYOUT_GAP_Y + idx * (NODE_H + LAYOUT_GAP_Y),
                width: NODE_W_WIDE,
                height: NODE_H,
            });
        }
        else {
            positions.set(n.id, {
                x: LAYOUT_GAP_X + idx * (NODE_W + LAYOUT_GAP_X),
                y: LAYOUT_GAP_Y,
                width: NODE_W,
                height: NODE_H,
            });
        }
    });
    return positions;
};
export const connectorPoints = (positions, fromId, toId) => {
    const a = positions.get(fromId);
    const b = positions.get(toId);
    if (!a || !b)
        return null;
    const aCx = a.x + a.width / 2;
    const aCy = a.y + a.height / 2;
    const bCx = b.x + b.width / 2;
    const bCy = b.y + b.height / 2;
    return { x1: aCx, y1: aCy, x2: bCx, y2: bCy, routed: 'orthogonal' };
};
//# sourceMappingURL=layout.js.map